import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Trash2 } from "lucide-react";

const INPUT = "w-full rounded border border-white/10 bg-[#050505] px-3 py-2 text-sm font-mono text-white focus:outline-none focus:border-rose-400/60";

// Mirrors radar_sync/sequencer.SYNCED.
export const SYNCED_COLLECTIONS = [
    ["ripeness_evidence", "Evidence"],
    ["ripeness_tracks", "Tracks"],
    ["ripeness_evaluations", "Evaluations"],
    ["ripeness_programs", "Programs"],
    ["news_api_ai", "NewsAPI.ai records"],
    ["ripeness_registry_snapshots", "Registry snapshots"],
    ["ripeness_record_links", "Record links"],
    ["ripeness_backfill_jobs", "Job & batch summaries"],
    ["ripeness_area_overrides", "Area overrides"],
    ["ripeness_area_taxonomy", "Custom areas"],
    ["ripeness_explanation_summaries", "AI summaries"],
    ["ripeness_external_docs", "Extra-source documents"],
    ["ripeness_external_fetch", "Extra-source fetch state"],
    ["ripeness_sponsor_profiles", "Sponsor pipeline pages"],
    ["ripeness_pipeline_finder", "Pipeline finder agent state"],
    ["ripeness_area_proposals", "Area proposals"],
    ["ripeness_area_refresh_runs", "Area refresh runs"],
    ["datalake_contexts", "Data Lake contexts"],
];
const LABEL = Object.fromEntries(SYNCED_COLLECTIONS);
const scopeText = (colls) => (colls?.length ? colls.map((c) => LABEL[c] || c).join(", ") : "all Radar data");

/** Type-the-name confirmation for deleting a spoke's Radar replica data (then re-downloading it). */
export function WipeReplicaDialog({ envName, onClose, onConfirm, canPickCollection = true }) {
    const [typed, setTyped] = useState("");
    const [scope, setScope] = useState("all");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await onConfirm(typed.trim(), scope === "all" ? null : [scope]);
            onClose();
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent className="bg-[#0c0c10] border-rose-400/30 text-white max-w-lg" data-testid="wipe-replica-dialog">
                <DialogHeader>
                    <DialogTitle className="inline-flex items-center gap-2"><Trash2 className="w-4 h-4 text-rose-300" /> Delete Radar replica data</DialogTitle>
                    <DialogDescription className="text-white/60 text-xs leading-relaxed">
                        <span className="text-white/90 font-mono">{envName}</span> first sends any changes the hub doesn't have yet (if that fails, nothing is deleted),
                        then deletes {scope === "all" ? "its programs, tracks, evaluations, evidence, registry snapshots, record links, backfill jobs, area overrides, area taxonomy, AI summaries and NewsAPI.ai records"
                            : <>its <span className="text-white/90">{LABEL[scope]}</span> only</>} and downloads a fresh copy from the hub. Admins, tenants, API keys, settings and the hub link stay.
                        Radar pages there are incomplete until the download finishes.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <label className="block">
                        <span className="label-xs block mb-1">What to refresh</span>
                        <select value={scope} onChange={(e) => setScope(e.target.value)} disabled={!canPickCollection} className={INPUT} data-testid="wipe-replica-scope-select">
                            <option value="all">{`All ${SYNCED_COLLECTIONS.length} collections`}</option>
                            {SYNCED_COLLECTIONS.map(([c, l]) => <option key={c} value={c}>{`${l} only`}</option>)}
                        </select>
                        {!canPickCollection && <span className="text-[11px] text-amber-200/80 mt-1 block" data-testid="wipe-replica-scope-note">Single-collection refresh needs the newer build on this environment — redeploy it first.</span>}
                    </label>
                    <label className="block">
                        <span className="label-xs block mb-1">Type <span className="font-mono text-rose-200">{envName}</span> to confirm</span>
                        <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} className={INPUT} placeholder={envName} data-testid="wipe-replica-confirm-input" />
                    </label>
                    <DialogFooter>
                        <button type="button" onClick={onClose} disabled={busy} className="px-3 py-1.5 text-xs text-white/60 hover:text-white" data-testid="wipe-replica-cancel">Cancel</button>
                        <button type="submit" disabled={busy || typed.trim() !== envName}
                                className="inline-flex items-center gap-1.5 rounded bg-rose-600 hover:bg-rose-500 px-4 py-1.5 text-xs text-white disabled:opacity-40 transition-colors"
                                data-testid="wipe-replica-submit">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} {scope === "all" ? "Delete and re-download" : `Refresh ${LABEL[scope]}`}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

const WIPE_STATE = {
    pushing: "Sending unsynced changes to the hub…",
    deleting: "Deleting replica data…",
    downloading: "Downloading a fresh copy from the hub…",
    done: "Replica data deleted and re-downloaded",
    failed: "Delete did not run",
};

/** One line describing a spoke's last replica wipe (from its status or heartbeat). */
export function WipeStatusLine({ wipe, pending, pendingCollections, testId }) {
    if (pending) return <div className="text-[11px] text-rose-200" data-testid={testId}>Delete + re-download of {scopeText(pendingCollections)} requested — runs within 30 s</div>;
    if (!wipe) return null;
    const tone = wipe.status === "failed" || wipe.error ? "text-amber-200" : wipe.status === "done" ? "text-emerald-200" : "text-sky-200";
    return (
        <div className={`text-[11px] ${tone}`} data-testid={testId}>
            {WIPE_STATE[wipe.status] || wipe.status}{wipe.collections?.length ? ` (${scopeText(wipe.collections)})` : ""}
            {wipe.status === "done" && ` · ${Number(wipe.deleted_total || 0).toLocaleString()} documents replaced`}
            {wipe.finished_at && ` · ${new Date(wipe.finished_at).toLocaleString()}`}
            {wipe.requested_by && ` · by ${wipe.requested_by}${wipe.origin === "hub" ? " (hub)" : ""}`}
            {wipe.error && <span className="block break-all">{wipe.error}</span>}
        </div>
    );
}
